import classNames from "classnames";
import { ReactComponent as ShareIcon } from "assets/icons/ic_share-2.svg";
import LoadingIndicator from "components/partial/loading-indicator/loading-indicator";
import noImage from "assets/images/no-image.png";
import isEmpty from "lodash/isEmpty";
import { observer } from "mobx-react";
import { IGetRecordsResponse } from "models/dto/ZoomiLxp/Models/Common/IGetRecordsResponse";
import { ICertificationRequirementModel } from "models/dto/ZoomiLxp/Models/GroupModels/ICertificationRequirementModel";
import { IGroupProfileItemModel } from "models/dto/ZoomiLxp/Models/GroupModels/IGroupProfileItemModel";
import { IQueryParams } from "models/dto/ZoomiLxp/Models/Query/IQueryParams";
import { MouseEvent, useState } from "react";
import { Cell, ColumnGroup } from "react-table";
import { generatePath, NavLink } from "react-router-dom";
import { Routes } from "routes";
import { useAppStore } from "store";
import styles from "./styles.module.scss";

export enum GroupsCertificationsTab {
	AllGroups = "AllGroups",
	MyGroupsCertifications = "MyGroupsCertifications",
}

export const groupsCertificationsTabs = [
	GroupsCertificationsTab.AllGroups,
	GroupsCertificationsTab.MyGroupsCertifications,
];

export const GROUPS_CERTIFICATIONS_PAGE_SIZE = 10;

export const getInitialParams = (): IQueryParams => ({
	take: GROUPS_CERTIFICATIONS_PAGE_SIZE,
	skip: 0,
	filterCriteria: [],
	sortCriteria: [],
	queryString: "",
});

export const emptyGroupsResult: IGetRecordsResponse<IGroupProfileItemModel> = {
	records: [],
	totalFilteredRecords: 0,
};

const formatPercent = (value?: number) => (typeof value === "number" ? `${value}%` : "-");

const getCertificateIdFromUrl = (url?: string) => {
	if (!url) return undefined;

	const urlWithoutQuery = url.split("?")[0].replace(/\/+$/, "");
	const certId = urlWithoutQuery.substring(urlWithoutQuery.lastIndexOf("/") + 1);

	return !isEmpty(certId) ? certId : undefined;
};

export const RequirementCell = ({ requirement }: { requirement?: ICertificationRequirementModel }) => (
	<span
		className={classNames(styles.groups_certifications__percent, {
			[styles.groups_certifications__percent_success]: requirement?.isSatisfied,
			[styles.groups_certifications__percent_error]: requirement && !requirement.isSatisfied,
		})}
	>
		{formatPercent(requirement?.currentValue)}
	</span>
);

export const CompletionCell = ({ item }: { item: IGroupProfileItemModel }) => (
	<span
		className={classNames(styles.groups_certifications__percent, {
			[styles.groups_certifications__percent_success]: item.progressRequirement?.isSatisfied,
			[styles.groups_certifications__percent_error]: item.progressRequirement && !item.progressRequirement.isSatisfied,
		})}
	>
		{formatPercent(item.completionPercent)}
	</span>
);

export const CertificateLink = observer(({ url }: { url?: string }) => {
	const { certificatesStore } = useAppStore();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const certId = getCertificateIdFromUrl(url);

	if (!certId) return <></>;

	const handleClick = async (event: MouseEvent<SVGSVGElement>) => {
		event.stopPropagation();
		setIsLoading(true);
		const certUrl = await certificatesStore.getCertificatePdfUrl(certId);
		setIsLoading(false);
		window.open(certUrl, "_blank");
	};

	return (
		<LoadingIndicator
			loading={isLoading}
			spinnerPosition="center"
			className={styles.groups_certifications__certificate_loader}
		>
			{!isLoading && <ShareIcon className={styles.share_icon} onClick={handleClick} />}
		</LoadingIndicator>
	);
});

export const GroupsCertificationsName = ({ item }: { item: IGroupProfileItemModel }) => (
	<NavLink
		to={generatePath(Routes.GroupPage, { id: item.id })}
		className={styles.groups_certifications__name_cell}
		onClick={(event) => event.stopPropagation()}
	>
		<img
			src={!isEmpty(item.thumbnail?.url) ? item.thumbnail.url : noImage}
			alt=""
			className={styles.groups_certifications__thumbnail}
		/>
		<span className={styles.groups_certifications__name}>{item.displayName}</span>
	</NavLink>
);

export const Columns = (): ColumnGroup<IGroupProfileItemModel>[] => [
	{
		Header: "Groups",
		columns: [
			{
				Header: "Name",
				id: "name",
				accessor: (row: IGroupProfileItemModel) => row,
				Cell({ cell }: { cell: Cell<IGroupProfileItemModel> }) {
					return <GroupsCertificationsName item={cell.row.original} />;
				},
			},
			{
				Header: "Group name",
				accessor: "groupName",
			},
			{
				Header: "Courses",
				accessor: "courseCount",
			},
			{
				Header: "Completion",
				id: "completion",
				accessor: (row: IGroupProfileItemModel) => row,
				Cell({ cell }: { cell: Cell<IGroupProfileItemModel> }) {
					return <CompletionCell item={cell.row.original} />;
				},
			},
			{
				Header: "Z1",
				id: "z1",
				accessor: (row: IGroupProfileItemModel) => row.z1Requirement,
				Cell({ cell: { value } }: { cell: Cell<IGroupProfileItemModel, ICertificationRequirementModel> }) {
					return <RequirementCell requirement={value} />;
				},
			},
			{
				Header: "Engagement",
				id: "engagement",
				accessor: (row: IGroupProfileItemModel) => row.engagementRequirement,
				Cell({ cell: { value } }: { cell: Cell<IGroupProfileItemModel, ICertificationRequirementModel> }) {
					return <RequirementCell requirement={value} />;
				},
			},
			{
				Header: "Exam pass %",
				id: "exam",
				accessor: (row: IGroupProfileItemModel) => row.examRequirement,
				Cell({ cell: { value } }: { cell: Cell<IGroupProfileItemModel, ICertificationRequirementModel> }) {
					return <RequirementCell requirement={value} />;
				},
			},
			{
				Header: "Certificate",
				id: "certificate",
				accessor: "certificateUrl",
				Cell({ cell: { value } }: { cell: Cell<IGroupProfileItemModel, string> }) {
					return (
						<div className={styles.groups_certifications__certificate_cell}>
							<CertificateLink url={value} />
						</div>
					);
				},
			},
		],
	},
];
